import { useCallback, useEffect, useSyncExternalStore } from "react";
import {
  getUserCoords,
  subscribeUserCoords,
  isLikelyUS,
  requestUserCoords,
  clearUserCoords,
} from "@/lib/user-location";

/**
 * Device coordinates from a URL search param: finite, in range, else undefined.
 * Shared by every route that accepts them so one validator can't drift from
 * another.
 */
export const coordParam = (v: unknown, lo: number, hi: number): number | undefined => {
  const n = typeof v === "number" ? v : (typeof v === "string" ? Number(v) : NaN);
  return Number.isFinite(n) && n >= lo && n <= hi ? n : undefined;
};

/**
 * Puts the visitor's coordinates into the route's search params, so the
 * server-side loader can see them (the geolocation store is client-only — the
 * URL is the only channel to a loader).
 *
 * Two shapes, because two kinds of page want different things:
 *
 *  - /search has no location of its own, so coords SCOPE it: pass
 *    `radiusMiles` and the results become a circle around the visitor.
 *  - /providers/... names its city in the path, the <h1>, and the canonical.
 *    Coords there must only measure distance, never move the centre — omit
 *    `radiusMiles` and the loader passes them as a distance reference instead
 *    (see SearchLoaderDeps.distanceOnly).
 *
 * Auto-apply is silent and never prompts: it acts only on coordinates the store
 * already has, i.e. a visitor who granted permission earlier. The cold prompt
 * only ever comes from `useMyLocation`, on a real click — a browser denial is
 * sticky and un-reaskable, so one badly-timed ask costs the feature for good.
 */
export function useNearMe({
  navigate,
  lat,
  lng,
  radiusMiles,
  skip,
}: {
  navigate: (opts: { search: (prev: any) => any; replace?: boolean }) => void;
  lat: number | undefined;
  lng: number | undefined;
  /** Scope results to this radius. Omit to stamp distances without scoping. */
  radiusMiles?: number;
  /** Don't auto-apply (e.g. the visitor typed their own location). */
  skip?: boolean;
}) {
  const coords = useSyncExternalStore(subscribeUserCoords, getUserCoords, () => null);

  const apply = useCallback(
    (c: { lat: number; lng: number }, clearTypedLocation: boolean) => {
      navigate({
        search: (prev: any) => ({
          ...prev,
          ...(clearTypedLocation ? { location: undefined } : {}),
          lat: c.lat,
          lng: c.lng,
          ...(radiusMiles ? { radius: prev.radius ?? radiusMiles } : {}),
          page: undefined,
        }),
        replace: true,
      });
    },
    [navigate, radiusMiles],
  );

  useEffect(() => {
    // A location the visitor named beats coordinates already in the URL, so
    // this drops them rather than just declining to add more. Without it,
    // typing "dentists in Boston" while near-me was active kept searching
    // around the visitor under a "Within 25 mi of Boston" banner.
    //
    // Only on scoping routes (radiusMiles set). On a landing page the
    // coordinates merely stamp distances and contradict nothing, so a typed
    // location has no conflict to resolve. The session store is left alone —
    // this is one search preferring a named place, not the visitor turning
    // near-me off, so the chip comes back as soon as the location does.
    if (skip) {
      if (radiusMiles && (lat != null || lng != null)) {
        navigate({
          search: (prev: any) => ({ ...prev, lat: undefined, lng: undefined, radius: undefined, page: undefined }),
          replace: true,
        });
      }
      return;
    }
    if (lat != null && lng != null) return;
    // Our provider data is US-only. Applying a foreign fix would either scope
    // the search to nothing or hang a "3,000 mi away" badge on every card.
    if (!coords || !isLikelyUS(coords)) return;
    apply(coords, false);
  }, [coords, lat, lng, skip, apply, navigate, radiusMiles]);

  /**
   * Explicit "use my location" — prompts if the browser hasn't decided yet.
   * Clearing a typed location is right here even though auto-apply won't do it:
   * clicking the button IS the visitor saying they'd rather search from where
   * they are. Only meaningful when the location is a search param; a route that
   * carries its city in the path has nothing to clear.
   */
  const useMyLocation = useCallback(async () => {
    const c = await requestUserCoords();
    if (!c) return; // denied — SearchBar surfaces the hint from the store status
    apply(c, Boolean(radiusMiles));
  }, [apply, radiusMiles]);

  /**
   * Clearing has to drop the session coords too, not just the URL params —
   * otherwise the auto-apply effect immediately puts them back.
   */
  const clearMyLocation = useCallback(() => {
    clearUserCoords();
    navigate({
      search: (prev: any) => ({ ...prev, lat: undefined, lng: undefined, radius: undefined, page: undefined }),
      replace: true,
    });
  }, [navigate]);

  return { useMyLocation, clearMyLocation };
}
